/**
 * sentry.js — Initialisation de la remontée d'erreurs frontend (Sentry)
 *
 * No-op si REACT_APP_SENTRY_DSN n'est pas défini au build (dev local, tests).
 * Le DSN navigateur est public par conception (il finit dans le bundle JS) et
 * versionné dans frontend/.env.production (D60 complétée, S5).
 *
 * Aucune donnée personnelle volontaire : sendDefaultPii false, pas de Replay,
 * pas de traces. Les URL (page, requêtes) et les messages peuvent pourtant
 * contenir un email ou un jeton (lien de réinitialisation `?token=`,
 * vérification d'email) : beforeSend et beforeBreadcrumb les masquent avant
 * tout envoi.
 */
import * as Sentry from '@sentry/react';

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Paramètres d'URL porteurs de secrets ou d'identité
const SENSITIVE_PARAM_RE = /([?&#](?:token|code|refreshToken|refresh_token|access_token|password|email)=)[^&#\s]*/gi;
const FILTERED = '[Filtered]';

/** Masque emails et paramètres sensibles d'une chaîne. */
export function scrubString(value) {
  if (typeof value !== 'string') return value;
  return value.replace(SENSITIVE_PARAM_RE, `$1${FILTERED}`).replace(EMAIL_RE, FILTERED);
}

/** Copie profonde où chaque chaîne est masquée (objets et tableaux simples). */
function scrubDeep(value, depth = 0) {
  if (typeof value === 'string') return scrubString(value);
  if (depth > 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = scrubDeep(v, depth + 1);
  return out;
}

export function scrubEvent(event) {
  const clean = scrubDeep(event);
  // Aucune identité utilisateur : ni email, ni IP, ni identifiant
  delete clean.user;
  return clean;
}

export const scrubBreadcrumb = (breadcrumb) => scrubDeep(breadcrumb);

/**
 * @param {Object} env process.env (injectable pour les tests)
 * @returns {boolean} true si Sentry a été initialisé
 */
export function initSentry(env = process.env) {
  if (!env.REACT_APP_SENTRY_DSN) return false;
  const buildHash = env.REACT_APP_BUILD_HASH;
  Sentry.init({
    dsn:              env.REACT_APP_SENTRY_DSN,
    environment:      env.NODE_ENV || 'development',
    // Hash git du build (injecté par scripts/inject-build-info.js au prebuild) :
    // relie une erreur à la version déployée, comme CACHE_VERSION.
    release:          buildHash && buildHash !== 'dev' ? `ekynair-web@${buildHash}` : undefined,
    tracesSampleRate: 0,
    sendDefaultPii:   false,
    beforeSend:       scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  return true;
}
